import { useCallback } from 'react';
import axios, { useApiRequest } from './axios';
import {
  CardSetupResult,
  PaymentMethod,
  SubscriptionChangePreview,
  TenantSubscription,
} from '../types/subscriptions';

const CHANGE_SUBSCRIPTION_URL = '/payment/change-tenant-subscription/';
const SUBSCRIPTION_URL = '/payment/subscription/';
const PREVIEW_CHANGE_URL = '/payment/subscription/preview-change/';
const CANCEL_SUBSCRIPTION_URL = '/payment/subscription/cancel/';
const RESUME_SUBSCRIPTION_URL = '/payment/subscription/resume/';
const CARD_URL = '/payment/subscription/card/';
const PAYMENT_METHOD_URL = '/payment/subscription/payment-method/';
const ENTERPRISE_INQUIRY_URL = '/payment/enterprise-inquiry/';

export const useChangeTenantSubscription = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const change = useCallback(
    async (data: { tariff: string }) => {
      return await executeRequest(() => axios.post(CHANGE_SUBSCRIPTION_URL, data));
    },
    [executeRequest]
  );

  return { loading, error, change };
};

export const useSubscription = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const query = useCallback(async (): Promise<TenantSubscription | null> => {
    const { data, success } = await executeRequest(() => axios.get(SUBSCRIPTION_URL));
    return success ? data : null;
  }, [executeRequest]);

  return { loading, error, query };
};

export const useCancelSubscription = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const cancel = useCallback(
    async () => executeRequest(() => axios.post(CANCEL_SUBSCRIPTION_URL)),
    [executeRequest]
  );

  return { loading, error, cancel };
};

export const useResumeSubscription = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const resume = useCallback(
    async () => executeRequest(() => axios.post(RESUME_SUBSCRIPTION_URL)),
    [executeRequest]
  );

  return { loading, error, resume };
};

export const useSetupCard = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const setupCard = useCallback(
    async (token: string): Promise<CardSetupResult | null> => {
      const { data, success } = await executeRequest(() =>
        axios.post(CARD_URL, { token })
      );
      return success ? (data as CardSetupResult) : null;
    },
    [executeRequest]
  );

  return { loading, error, setupCard };
};

export const useRemoveCard = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const removeCard = useCallback(
    async () => executeRequest(() => axios.delete(CARD_URL)),
    [executeRequest]
  );

  return { loading, error, removeCard };
};

export const useSetPaymentMethod = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const setPaymentMethod = useCallback(
    async (payment_method: PaymentMethod) =>
      executeRequest(() => axios.post(PAYMENT_METHOD_URL, { payment_method })),
    [executeRequest]
  );

  return { loading, error, setPaymentMethod };
};

export const useSubscriptionChangePreview = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const preview = useCallback(
    async (tariff: string): Promise<SubscriptionChangePreview | null> => {
      const { data, success } = await executeRequest(() =>
        axios.post(PREVIEW_CHANGE_URL, { tariff })
      );
      return success ? data : null;
    },
    [executeRequest]
  );

  return { loading, error, preview };
};

export const useEnterpriseInquiry = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const submit = useCallback(
    async (data: { contact: string; preferred_contact_at?: string; note?: string }) => {
      return await executeRequest(() => axios.post(ENTERPRISE_INQUIRY_URL, data));
    },
    [executeRequest]
  );

  return { loading, error, submit };
};
